import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Modal,
  message,
  Tag,
  Select,
  Popconfirm,
  Tooltip,
  Spin,
  Alert,
  Typography,
  Divider,
  Row,
  Col, // Updated imports
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ContainerOutlined,
  CalendarOutlined,
  MessageOutlined, // Added specific icons
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext"; // To check roles for actions
import {
  getQuotes,
  addQuote,
  updateQuote,
  deleteQuote,
  updateQuoteStatus,
} from "../../services/quoteService";
import QuoteForm from "../../components/quotes/QuoteForm"; // Import the form component

const { Search } = Input;
const { Title, Text, Paragraph } = Typography; // Destructure Typography components

// Status options needed for rendering Tags and the inline Status Select in this component
const statusOptionsForTag = [
  // Renamed to avoid confusion with options possibly defined in QuoteForm
  {
    value: "Pending",
    label: "Pending",
    color: "orange",
    icon: <ClockCircleOutlined />,
  },
  {
    value: "Confirmed",
    label: "Confirmed",
    color: "green",
    icon: <CheckCircleOutlined />,
  },
  {
    value: "Processing",
    label: "Processing",
    color: "blue",
    icon: <ClockCircleOutlined />,
  },
  {
    value: "Completed",
    label: "Completed",
    color: "purple",
    icon: <CheckCircleOutlined />,
  },
  {
    value: "Rejected",
    label: "Rejected",
    color: "red",
    icon: <CloseCircleOutlined />,
  },
];

// Helper function to render status tags based on the options above
const getStatusTag = (status) => {
  const statusConfig = statusOptionsForTag.find((s) => s.value === status) || {
    color: "default",
    label: status,
    icon: null,
  };
  return (
    <Tag icon={statusConfig.icon} color={statusConfig.color}>
      {statusConfig.label}
    </Tag>
  );
};

const QuoteListPage = () => {
  // --- State Variables ---
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({}); // For table-level filtering if needed
  const [sorter, setSorter] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  // Add/Edit Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  // View Modal State
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingQuote, setViewingQuote] = useState(null);

  const { isAdmin, isManager } = useAuth(); // Get roles for RBAC

  // --- Data Fetching ---
  const fetchData = useCallback(
    async (params = {}) => {
      console.log("Fetching quotes with params:", params); // Debug log
      setLoading(true);
      try {
        const queryParams = {
          page: params.pagination?.current || pagination.current,
          pageSize: params.pagination?.pageSize || pagination.pageSize,
          search: params.search !== undefined ? params.search : searchTerm,
          sortField: params.sorter?.field || sorter.field,
          sortOrder: params.sorter?.order || sorter.order,
          ...params.filters,
        };
        const result = await getQuotes(queryParams);
        setQuotes(result.data);
        setPagination((prev) => ({
          ...prev,
          current: queryParams.page,
          pageSize: queryParams.pageSize,
          total: result.total,
        }));
      } catch (error) {
        message.error(`Failed to fetch quotes: ${error.message}`);
        console.error("Fetch quotes error:", error);
      } finally {
        setLoading(false);
        console.log("Fetching finished."); // Debug log
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      pagination.current,
      pagination.pageSize,
      searchTerm,
      sorter.field,
      sorter.order,
    ]
  ); // Minimal stable dependencies

  // Effect for initial load and certain state changes
  useEffect(() => {
    fetchData({ pagination, sorter, filters, search: searchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchTerm]); // Trigger on these changes

  // --- Event Handlers ---

  const handleTableChange = (newPagination, tableFilters, newSorter) => {
    // Call fetchData directly with the new parameters
    fetchData({
      pagination: newPagination,
      filters: tableFilters,
      sorter: newSorter,
      search: searchTerm,
    });
    // Update state *after* initiating fetch to reflect changes in UI controls
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
    setFilters(tableFilters);
    setSorter(newSorter);
  };

  const handleSearch = (value) => {
    const newSearchTerm = value;
    setSearchTerm(newSearchTerm);
    // Fetch data directly, resetting to page 1
    fetchData({
      pagination: { ...pagination, current: 1 },
      search: newSearchTerm,
      sorter: sorter,
      filters: filters,
    });
  };

  const handleRefresh = () => {
    const resetParams = {
      pagination: { ...pagination, current: 1 },
      search: "",
      sorter: {},
      filters: {},
    };
    setSearchTerm("");
    setFilters({});
    setSorter({});
    // Fetch data directly with reset parameters
    fetchData(resetParams);
  };

  // Add/Edit Modal Handlers
  const showAddModal = () => {
    if (!isAdmin && !isManager) {
      message.error("Unauthorized");
      return;
    }
    setEditingQuote(null);
    setIsModalVisible(true);
  };

  const showEditModal = (quote) => {
    if (!isAdmin && !isManager) {
      message.error("Unauthorized");
      return;
    }
    setEditingQuote(quote);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingQuote(null);
  };

  // View Modal Handlers
  const showViewModal = (quote) => {
    setViewingQuote(quote);
    setIsViewModalVisible(true);
  };

  const handleViewCancel = () => {
    setIsViewModalVisible(false);
    setTimeout(() => setViewingQuote(null), 300); // Clear after animation
  };

  // Form Submission Handler (from QuoteForm)
  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      let successMessage = "";
      if (editingQuote) {
        await updateQuote(editingQuote.id, values);
        successMessage = "Quote updated successfully!";
      } else {
        await addQuote(values);
        successMessage = "Quote added successfully!";
      }
      message.success(successMessage);
      setIsModalVisible(false);
      setEditingQuote(null);
      handleRefresh(); // Refresh list after add/edit
    } catch (error) {
      message.error(`Operation failed: ${error.message || "Unknown error"}`);
      setLoading(false);
    }
  };

  // Delete Handler
  const handleDelete = async (id) => {
    if (!isAdmin) {
      message.error("Unauthorized");
      return;
    }
    setLoading(true);
    try {
      await deleteQuote(id);
      message.success("Quote deleted successfully!");
      const newTotal = pagination.total - 1;
      let newCurrent = pagination.current;
      if (
        newTotal > 0 &&
        (pagination.current - 1) * pagination.pageSize >= newTotal
      ) {
        newCurrent = Math.max(1, pagination.current - 1);
      }
      fetchData({
        pagination: { ...pagination, total: newTotal, current: newCurrent },
        sorter: sorter,
        filters: filters,
        search: searchTerm,
      });
    } catch (error) {
      message.error(`Failed to delete quote: ${error.message}`);
      setLoading(false);
    }
  };

  // Status Change Handler
  const handleStatusChange = async (quoteId, newStatus) => {
    if (!isAdmin && !isManager) {
      message.error("Unauthorized");
      return;
    }
    setLoading(true);
    try {
      await updateQuoteStatus(quoteId, newStatus);
      message.success("Quote status updated!");
      fetchData({ pagination, sorter, filters, search: searchTerm }); // Refresh current view
    } catch (error) {
      message.error(`Failed to update status: ${error.message}`);
      setLoading(false);
    }
  };

  // --- Table Columns Definition ---
  const columns = [
    { title: "Client Name", dataIndex: "name", key: "name", sorter: true },
    { title: "Email", dataIndex: "email", key: "email", sorter: true },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    {
      title: "Project Type",
      dataIndex: "projectType",
      key: "projectType",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: getStatusTag, // Use local tag renderer
      filters: statusOptionsForTag.map((s) => ({
        text: s.label,
        value: s.value,
      })), // Use local options for filters
      sorter: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 350,
      fixed: "right",
      render: (_, record) => (
        <Space size="small" wrap>
          {/* View Button */}
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showViewModal(record)}
              disabled={loading}
            />
          </Tooltip>
          {/* Change Status Select */}
          {(isAdmin || isManager) && (
            <Select
              value={record.status}
              size="small"
              style={{ width: 120 }}
              onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
              // Use local options array for the inline select
              options={statusOptionsForTag.map((s) => ({
                value: s.value,
                label: s.label,
              }))}
              disabled={loading}
            />
          )}
          {/* Edit Button */}
          {(isAdmin || isManager) && (
            <Tooltip title="Edit Quote">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => showEditModal(record)}
                disabled={loading}
              />
            </Tooltip>
          )}
          {/* Delete Button */}
          {isAdmin && (
            <Tooltip title="Delete Quote">
              <Popconfirm
                title="Are you sure delete this quote?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
                disabled={loading}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  disabled={loading}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // --- Render Logic ---
  return (
    <div>
      {/* Header Section */}
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Space>
          <Search
            placeholder="Search quotes..."
            onSearch={handleSearch}
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            style={{ width: 300 }}
            allowClear
            enterButton={<SearchOutlined />}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
        {(isAdmin || isManager) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            {" "}
            Add Quote{" "}
          </Button>
        )}
      </Space>

      {/* Quotes Table */}
      <Table
        columns={columns}
        dataSource={quotes}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingQuote ? "Edit Quote" : "Add New Quote"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={720}
      >
        <QuoteForm
          key={editingQuote ? `edit-${editingQuote.id}` : "add"}
          initialValues={editingQuote}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Modal>

      {/* View Details Modal (Redesigned) */}
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            <EyeOutlined style={{ marginRight: 8 }} /> Quote Details
          </Title>
        }
        open={isViewModalVisible}
        onCancel={handleViewCancel}
        footer={[
          <Button key="close" type="primary" onClick={handleViewCancel}>
            {" "}
            Close{" "}
          </Button>,
        ]}
        width={680}
        bodyStyle={{ paddingTop: "20px" }}
      >
        {viewingQuote ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <UserOutlined /> Client Name
                  </Text>
                  <Text strong style={{ fontSize: "1.1em" }}>
                    {viewingQuote.name}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <MailOutlined /> Client Email
                  </Text>
                  <Text strong style={{ fontSize: "1.1em" }}>
                    {viewingQuote.email}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <PhoneOutlined /> Client Phone
                  </Text>
                  <Text strong style={{ fontSize: "1.1em" }}>
                    {viewingQuote.phone}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <ContainerOutlined /> Project Type
                  </Text>
                  <Text strong style={{ fontSize: "1.1em" }}>
                    {viewingQuote.projectType}
                  </Text>
                </Space>
              </Col>
            </Row>

            <Divider orientation="left" plain>
              Quote Information
            </Divider>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">Status</Text>
                  <div>{getStatusTag(viewingQuote.status)}</div>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <CalendarOutlined /> Created At
                  </Text>
                  <Text strong>
                    {viewingQuote.createdAt
                      ? new Date(viewingQuote.createdAt).toLocaleString()
                      : "-"}
                  </Text>
                </Space>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    <MessageOutlined /> Message / Requirements
                  </Text>
                  <Paragraph
                    style={{
                      paddingLeft: "5px",
                      borderLeft: "3px solid #eee",
                      margin: 0,
                      backgroundColor: "#fafafa",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        margin: 0,
                        fontFamily: "inherit",
                        fontSize: "0.95em",
                      }}
                    >
                      {viewingQuote.message || "(No message provided)"}
                    </pre>
                  </Paragraph>
                </Space>
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "30px" }}>
            <Spin />
            <p>Loading details...</p>
          </div>
        )}
      </Modal>
    </div> // End of main wrapper div
  );
};

export default QuoteListPage;
